import { useEffect } from 'react';
import { useSelector } from 'react-redux';
import { useSortBy, useTable } from 'react-table';
import styled from 'styled-components';

import { IStoreState } from 'store/modules';

interface Props {
  columns: any;
  data: any;
  onClick?: (roomId: string, isExpire: boolean) => void;
  isActiveAlerts?: boolean;
}

const Table = ({ columns, data, onClick, isActiveAlerts = false }: Props) => {
  const selectedRoom = useSelector((store: IStoreState) => store.MainReducer.selectedRoom);
  const { getTableProps, getTableBodyProps, headerGroups, rows, prepareRow } = useTable(
    {
      columns,
      data,
      initialState: {
        sortBy: [
          {
            id: 'saveTime',
            desc: true,
          },
        ],
      },
    },
    useSortBy
  );

  const selectRoom = (data: any) => {
    if (!isActiveAlerts && onClick !== undefined) {
      onClick(data.original.roomId, data.original.isExpire);
    }
  };

  useEffect(() => {
    const previouslySelectedRow = document.querySelector('tr.selected');
    if (previouslySelectedRow) {
      previouslySelectedRow.classList.remove('selected');
    }
    if (!isActiveAlerts && selectedRoom !== undefined) {
      const rowIndex = rows.findIndex((data: any) => data.original.roomId === selectedRoom);
      const tableRows = document.querySelectorAll('tbody tr');
      if (rowIndex !== undefined && tableRows[rowIndex]) {
        tableRows[rowIndex].classList.add('selected');
      }
    }
  }, [rows, selectedRoom, isActiveAlerts]);

  return (
    <Styles $isClickable={!isActiveAlerts} $isActiveAlerts={isActiveAlerts}>
      <table {...getTableProps()}>
        <thead>
          {headerGroups.map((headerGroup, index) => (
            <tr {...headerGroup.getHeaderGroupProps()} key={index}>
              {headerGroup.headers.map((column, i) => (
                <th
                  {...column.getHeaderProps()}
                  key={i}
                  style={{
                    flex: `${column.width || 100} 1 0%`,
                    minWidth: '0px',
                  }}
                >
                  {column.render('Header')}
                </th>
              ))}
            </tr>
          ))}
        </thead>
        <tbody {...getTableBodyProps()}>
          {rows.map((row, index) => {
            prepareRow(row);
            return (
              <tr {...row.getRowProps()} key={index} onClick={() => selectRoom(row)}>
                {row.cells.map((cell, i) => (
                  <td
                    {...cell.getCellProps()}
                    key={i}
                    style={{
                      flex: `${cell.column.width || 100} 1 0%`,
                      minWidth: '0px',
                    }}
                  >
                    {cell.render('Cell')}
                  </td>
                ))}
              </tr>
            );
          })}
        </tbody>
      </table>
    </Styles>
  );
};

const Styles = styled.div<{ $isClickable: boolean; $isActiveAlerts: boolean }>`
  height: 100%;

  table {
    width: 100%;
    height: 100%;
    background: linear-gradient(180deg, #002f5a 0%, #000 100%);
    border-collapse: collapse;

    thead {
      display: block;
      width: 100%;
      color: #fff;
      text-align: center;
      font-family: Inter;
      font-size: 1rem;
      font-weight: 500;

      tr {
        display: flex;
        width: 100%;
      }

      th {
        vertical-align: middle;
        height: 2.56rem;
        border: 1px solid #6b6b6b;
        box-sizing: border-box;
        display: flex;
        align-items: center;
        justify-content: center;
      }
    }

    tbody {
      height: ${({ $isActiveAlerts }) => ($isActiveAlerts ? 'calc(100% - 45px)' : 'calc(100% - 90px)')};
      display: block;
      overflow-y: auto;
      overflow-x: hidden;

      ::-webkit-scrollbar {
        width: 3px;
      }
      ::-webkit-scrollbar-thumb {
        height: 10%;
        background: #0086ff;
        border-radius: 10px;
      }
      ::-webkit-scrollbar-track {
        background: rgba(33, 122, 244, 0.1);
      }
    }

    tr {
      display: flex;
      width: 100%;
      border-bottom: 1px solid #6b6b6b;
      box-sizing: border-box;

      &.selected {
        background: #004d94;
      }
    }

    td {
      display: flex;
      align-items: center;
      justify-content: center;
      height: 3.5rem;
      color: #fff;
      text-align: center;
      font-family: Inter;
      font-size: 1rem;
      font-weight: 500;
      border: 1px solid #6b6b6b;
      cursor: pointer;
      box-sizing: border-box;

      cursor: ${({ $isClickable }) => ($isClickable ? 'pointer' : 'default')};
      overflow: hidden;
      text-overflow: ellipsis;
      white-space: nowrap;

      :last-child {
        border-right: 0;
      }
    }
  }
`;

export default Table;
